/*!
 * Parenting Optimize V3.3 - Paid Blueprint App Controller
 * File: parenting-app-v3.js
 * Version: 3.3.0
 *
 * Purpose:
 * - Short assessment flow
 * - Back navigation on every screen after welcome
 * - Free personalized preview
 * - Paid Blueprint paywall
 * - Full paid Blueprint rendering
 * - Child profile memory
 * - Debug/testing mode
 *
 * URL helpers:
 * - ?po_debug=1  shows debug panel
 * - ?po_paid=1   unlocks paid Blueprint for testing
 */

(function (window, document) {
  "use strict";

  const PO = (window.PO = window.PO || {});
  PO.version = "3.3.0";

  const DEFAULT_STATE = () => ({
    childName: "",
    childAge: "",
    situationDescription: "",
    scenario: "",
    observations: [],
    adaptiveAnswers: {},
    adaptiveQuestions: [],
    engineResult: null,
    narrativeAnalysis: null,
    detectedObservations: [],
    paidUnlocked: false,
    startedAt: new Date().toISOString()
  });

  PO.AppV3 = {
    version: "3.3.0",

    sessionStorageKey: "PO_V33_ACTIVE_SESSION",
    profileStorageKey: "PO_V33_CHILD_PROFILES",
    historyStorageKey: "PO_V33_SESSION_HISTORY",

    currentScreen: 1,
    totalScreens: 18,
    state: DEFAULT_STATE(),
    debugMode: false,

    init() {
      this.root = document.querySelector("#po-app");
      if (!this.root) return;

      const params = new URLSearchParams(window.location.search);
      this.debugMode = params.get("po_debug") === "1";
      this.state = DEFAULT_STATE();
      this.state.paidUnlocked = params.get("po_paid") === "1";

      // Always begin at the start screen on a fresh page visit.
      this.currentScreen = 1;

      this.render();
      this.bind();
      this.showScreen(1, { save: false });

      this.emit("app:ready", {
        version: this.version,
        debugMode: this.debugMode,
        paidUnlocked: this.state.paidUnlocked
      });
    },

    // =========================================================================
    // RENDER
    // =========================================================================

    render() {
      this.root.classList.add("po-app-v3", "po-app-v33");

      this.root.innerHTML = `
        ${this.renderWelcome()}
        ${this.renderChild()}
        ${this.renderNarrative()}
        ${this.renderScenario()}
        ${this.renderObservations()}
        ${this.renderAdaptive(0)}
        ${this.renderAdaptive(1)}
        ${this.renderThinking()}
        ${this.renderFreePreview()}
        ${this.renderPaywall()}
        ${this.renderChildProfile()}
        ${this.renderHowWeReachedThis()}
        ${this.renderSituationAnalysis()}
        ${this.renderDetailedPlan()}
        ${this.renderScriptsExamples()}
        ${this.renderMotivationCoParenting()}
        ${this.renderSevenDayPlan()}
        ${this.renderProgressAndFeedback()}
        ${this.debugMode ? this.renderDebugPanel() : ""}
      `;

      this.hydrate();
    },

    renderWelcome() {
      return `
        <section class="po-screen po-cinematic-screen is-active" data-screen="1">
          <div class="po-phone">
            <p class="po-eyebrow">Parenting Optimize</p>
            <h1 class="po-title">Let’s understand what is really happening.</h1>
            <p class="po-copy">
              We’ll look beyond the behavior, ask a few smart questions, and build a personalized plan for your child.
            </p>
            <button class="po-main-btn" type="button" data-next>Begin</button>
          </div>
        </section>
      `;
    },

    renderChild() {
      return `
        <section class="po-screen" data-screen="2">
          <div class="po-phone">
            ${this.renderTopNav(2)}
            <h1 class="po-title">Who are we thinking about?</h1>
            <p class="po-copy">Name and age help us keep the guidance personal and age-appropriate.</p>

            <input
              class="po-input"
              id="po-child-name"
              name="childName"
              type="text"
              placeholder="Child’s name"
              autocomplete="off"
              data-required
            >

            <input
              class="po-input"
              id="po-child-age"
              name="childAge"
              type="number"
              inputmode="numeric"
              placeholder="Age"
              min="2"
              max="18"
              data-required
            >

            <div class="po-error" data-error>Please enter a name and an age from 2–18.</div>
            <button class="po-main-btn" type="button" data-next>Continue</button>
          </div>
        </section>
      `;
    },

    renderNarrative() {
      return `
        <section class="po-screen" data-screen="3">
          <div class="po-phone">
            ${this.renderTopNav(3)}
            <h1 class="po-title">What happened?</h1>
            <p class="po-copy">
              Describe it naturally. Write it the way you would explain it to someone who knows your family.
            </p>

            <textarea
              class="po-textarea"
              id="po-situation-description"
              name="situationDescription"
              placeholder="Example: Every night he asks for five more minutes, comes out of his room, and gets upset when I say no."
              data-required
            ></textarea>

            <div class="po-error" data-error>Please add a short description.</div>
            <button class="po-main-btn" type="button" data-next>Analyze this</button>
          </div>
        </section>
      `;
    },

    renderScenario() {
      const scenarios = this.getScenarios();

      return `
        <section class="po-screen" data-screen="4">
          <div class="po-phone">
            ${this.renderTopNav(4)}
            <h1 class="po-title">Which situation fits best?</h1>
            <p class="po-copy">Choose the closest match. The details you wrote still matter.</p>

            <div class="po-options po-scroll-options" data-choice-group="scenario">
              ${scenarios.map((item) => `
                <button class="po-option" type="button" data-value="${this.escapeAttr(item.id)}">
                  ${this.escapeHTML(item.label)}
                </button>
              `).join("")}
            </div>

            <div class="po-error" data-error>Please choose one situation.</div>
          </div>
        </section>
      `;
    },

    renderObservations() {
      return `
        <section class="po-screen" data-screen="5">
          <div class="po-phone">
            ${this.renderTopNav(5)}
            <h1 class="po-title">What stood out?</h1>
            <p class="po-copy">
              Choose every behavior that sounds familiar. These details help us separate similar explanations.
            </p>

            <div class="po-options po-scroll-options" data-multi-group="observations"></div>

            <div class="po-error" data-error>Please choose at least one behavior.</div>
            <button class="po-main-btn" type="button" data-next>Continue</button>
          </div>
        </section>
      `;
    },

    renderAdaptive(index) {
      const screen = index === 0 ? 6 : 7;

      return `
        <section class="po-screen" data-screen="${screen}">
          <div class="po-phone">
            ${this.renderTopNav(screen)}
            <p class="po-eyebrow">${index === 0 ? "One more thing" : "Let’s check one more angle"}</p>
            <h1 class="po-title" data-adaptive-question="${index}">
              ${index === 0 ? "This answer changes the recommendation." : "This helps us avoid the wrong conclusion."}
            </h1>

            <div class="po-options" data-adaptive-options="${index}"></div>

            <div class="po-error" data-error>Please choose the closest answer.</div>
          </div>
        </section>
      `;
    },

    renderThinking() {
      return `
        <section class="po-screen po-cinematic-screen" data-screen="8">
          <div class="po-phone po-thinking-wrap">
            ${this.renderTopNav(8, { subtle: true })}
            <p class="po-eyebrow">Looking for the pattern</p>
            <div class="po-thinking-lines">
              <div>Comparing the situation...</div>
              <div>Checking your child’s age...</div>
              <div>Weighing possible explanations...</div>
            </div>
            <div class="po-thinking-pulse" aria-hidden="true"></div>
          </div>
        </section>
      `;
    },

    renderFreePreview() {
      return `
        <section class="po-screen po-insight-screen" data-screen="9">
          <div class="po-phone">
            ${this.renderTopNav(9)}
            <div data-result="free-preview"></div>
            <button class="po-main-btn" type="button" data-next>See the full Blueprint</button>
          </div>
        </section>
      `;
    },

    renderPaywall() {
      return `
        <section class="po-screen po-insight-screen" data-screen="10">
          <div class="po-phone">
            ${this.renderTopNav(10)}
            <div data-result="paywall"></div>

            <button class="po-main-btn" type="button" data-unlock-blueprint>
              Unlock My Parenting Blueprint
            </button>

            <button class="po-link-btn" type="button" data-back>
              Return to my free insight
            </button>
          </div>
        </section>
      `;
    },

    renderChildProfile() {
      return this.renderPaidScreen({
        screen: 11,
        resultKey: "child-profile",
        button: "How did you reach this?"
      });
    },

    renderHowWeReachedThis() {
      return this.renderPaidScreen({
        screen: 12,
        resultKey: "how-reached",
        button: "Explain the situation"
      });
    },

    renderSituationAnalysis() {
      return this.renderPaidScreen({
        screen: 13,
        resultKey: "situation-analysis",
        button: "Show me the full plan"
      });
    },

    renderDetailedPlan() {
      return this.renderPaidScreen({
        screen: 14,
        resultKey: "detailed-plan",
        button: "Give me scripts and examples"
      });
    },

    renderScriptsExamples() {
      return this.renderPaidScreen({
        screen: 15,
        resultKey: "scripts-examples",
        button: "Rewards and co-parenting"
      });
    },

    renderMotivationCoParenting() {
      return this.renderPaidScreen({
        screen: 16,
        resultKey: "motivation-coparenting",
        button: "Show me the 7-day plan"
      });
    },

    renderSevenDayPlan() {
      return this.renderPaidScreen({
        screen: 17,
        resultKey: "seven-day-plan",
        button: "What should I track?"
      });
    },

    renderProgressAndFeedback() {
      return `
        <section class="po-screen po-insight-screen" data-screen="18">
          <div class="po-phone">
            ${this.renderTopNav(18)}
            <div data-result="progress-feedback"></div>

            <div class="po-actions-stack">
              <button class="po-main-btn" type="button" data-feedback="worked">This helped</button>
              <button class="po-secondary-btn" type="button" data-feedback="somewhat">It partly helped</button>
              <button class="po-secondary-btn" type="button" data-feedback="did-not-work">It did not help</button>
              <button class="po-link-btn" type="button" data-start-over>Start a new situation</button>
            </div>
          </div>
        </section>
      `;
    },

    renderPaidScreen(config) {
      return `
        <section class="po-screen po-insight-screen" data-screen="${config.screen}">
          <div class="po-phone">
            ${this.renderTopNav(config.screen)}
            <div data-result="${this.escapeAttr(config.resultKey)}"></div>
            <button class="po-main-btn" type="button" data-next>
              ${this.escapeHTML(config.button)}
            </button>
          </div>
        </section>
      `;
    },

    renderTopNav(screen, options = {}) {
      if (screen <= 1) return "";

      const percent = Math.round(((screen - 1) / (this.totalScreens - 1)) * 100);

      return `
        <div class="po-top-nav ${options.subtle ? "is-subtle" : ""}">
          <button class="po-back-btn" type="button" data-back aria-label="Go back">
            ← Back
          </button>
          <span class="po-progress-label">${screen} / ${this.totalScreens}</span>
        </div>
        <div class="po-progress-track" aria-hidden="true">
          <span style="width:${percent}%"></span>
        </div>
      `;
    },

    renderDebugPanel() {
      return `
        <aside class="po-debug-panel" data-debug-panel>
          <button type="button" class="po-debug-toggle" data-debug-toggle>Debug</button>
          <pre data-debug-output></pre>
        </aside>
      `;
    },

    // =========================================================================
    // EVENTS
    // =========================================================================

    bind() {
      this.root.addEventListener("click", (event) => {
        if (event.target.closest("[data-back]")) {
          event.preventDefault();
          this.back();
          return;
        }

        const next = event.target.closest("[data-next]");
        if (next) {
          event.preventDefault();
          this.next();
          return;
        }

        const choice = event.target.closest("[data-choice-group] [data-value]");
        if (choice) {
          event.preventDefault();
          this.selectChoice(choice);
          return;
        }

        const multi = event.target.closest("[data-multi-group] [data-value]");
        if (multi) {
          event.preventDefault();
          this.toggleMultiChoice(multi);
          return;
        }

        const adaptive = event.target.closest("[data-adaptive-options] [data-value]");
        if (adaptive) {
          event.preventDefault();
          this.selectAdaptiveAnswer(adaptive);
          return;
        }

        if (event.target.closest("[data-unlock-blueprint]")) {
          event.preventDefault();
          this.unlockBlueprint();
          return;
        }

        const feedback = event.target.closest("[data-feedback]");
        if (feedback) {
          event.preventDefault();
          this.handleFeedback(feedback.dataset.feedback);
          return;
        }

        if (event.target.closest("[data-start-over]")) {
          event.preventDefault();
          this.startOver();
          return;
        }

        if (event.target.closest("[data-debug-toggle]")) {
          event.preventDefault();
          this.root.querySelector("[data-debug-panel]")?.classList.toggle("is-open");
        }
      });

      this.root.addEventListener("input", (event) => {
        const field = event.target;
        if (!field.name) return;
        this.state[field.name] = String(field.value || "").trim();
        this.clearError(field.closest(".po-screen"));
        this.saveActiveSession();
      });
    },

    next() {
      const screen = this.getCurrentScreen();
      if (!screen || !this.validate(screen)) return;

      if (this.currentScreen === 3) {
        this.applyNarrativeAnalysis();
        this.refreshScenarioSelection();
        this.refreshObservationOptions();
      }

      if (this.currentScreen === 5) {
        this.prepareAdaptiveQuestions();
      }

      if (this.currentScreen === 7) {
        this.runEngine();
        this.showScreen(8);

        window.setTimeout(() => {
          this.refreshResults();
          this.showScreen(9);
        }, 1150);
        return;
      }

      if (this.currentScreen === 9) {
        this.showScreen(10);
        return;
      }

      if (this.currentScreen === 10 && !this.state.paidUnlocked) {
        return;
      }

      if (this.currentScreen < this.totalScreens) {
        this.showScreen(this.currentScreen + 1);
      }
    },

    back() {
      if (this.currentScreen <= 1) return;

      if (this.currentScreen === 9) {
        // Avoid returning to the timed thinking screen.
        this.showScreen(7);
        return;
      }

      if (this.currentScreen === 10) {
        this.showScreen(9);
        return;
      }

      if (this.currentScreen === 11) {
        this.showScreen(10);
        return;
      }

      this.showScreen(this.currentScreen - 1);
    },

    unlockBlueprint() {
      this.emit("blueprint:unlock-requested", {
        childName: this.state.childName,
        scenario: this.state.scenario,
        result: this.state.engineResult
      });

      // Testing/default behavior:
      // This immediately unlocks. Replace this with checkout success handling.
      this.state.paidUnlocked = true;
      this.saveActiveSession();
      this.persistChildProfile(this.state.engineResult);
      this.showScreen(11);
    },

    selectChoice(option) {
      const group = option.closest("[data-choice-group]");
      if (!group) return;

      const key = group.dataset.choiceGroup;
      const value = option.dataset.value;

      group.querySelectorAll("[data-value]").forEach((item) => {
        item.classList.toggle("is-selected", item === option);
      });

      this.state[key] = value;

      if (key === "scenario") {
        this.state.adaptiveQuestions = [];
        this.state.adaptiveAnswers = {};
        this.state.engineResult = null;
        this.refreshObservationOptions();
      }

      this.clearError(option.closest(".po-screen"));
      this.saveActiveSession();

      window.setTimeout(() => this.showScreen(this.currentScreen + 1), 160);
    },

    toggleMultiChoice(option) {
      const value = option.dataset.value;
      const list = Array.isArray(this.state.observations) ? this.state.observations : [];

      if (list.includes(value)) {
        this.state.observations = list.filter((item) => item !== value);
        option.classList.remove("is-selected");
      } else {
        this.state.observations = list.concat(value);
        option.classList.add("is-selected");
      }

      this.state.adaptiveQuestions = [];
      this.state.adaptiveAnswers = {};
      this.state.engineResult = null;

      this.clearError(option.closest(".po-screen"));
      this.saveActiveSession();
    },

    selectAdaptiveAnswer(option) {
      const group = option.closest("[data-adaptive-options]");
      if (!group) return;

      const index = Number(group.dataset.adaptiveOptions);
      const question = this.state.adaptiveQuestions[index];
      if (!question) return;

      group.querySelectorAll("[data-value]").forEach((item) => {
        item.classList.toggle("is-selected", item === option);
      });

      this.state.adaptiveAnswers[question.id] = {
        questionId: question.id,
        question: question.question,
        optionIndex: Number(option.dataset.index),
        value: option.dataset.value,
        label: option.textContent.trim(),
        measures: question.measures || []
      };

      this.state.engineResult = null;
      this.saveActiveSession();

      window.setTimeout(() => {
        if (index === 0) this.showScreen(7);
        else this.next();
      }, 160);
    },

    // =========================================================================
    // FLOW
    // =========================================================================

    showScreen(number, options = {}) {
      this.currentScreen = Math.min(Math.max(Number(number) || 1, 1), this.totalScreens);

      this.root.querySelectorAll(".po-screen").forEach((screen) => {
        screen.classList.toggle("is-active", Number(screen.dataset.screen) === this.currentScreen);
      });

      if ([6, 7].includes(this.currentScreen)) {
        this.prepareAdaptiveQuestions();
        this.refreshAdaptiveQuestions();
      }

      if (this.currentScreen >= 9) {
        this.refreshResults();
      }

      if (options.save !== false) {
        this.saveActiveSession();
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
      this.emit("screen:view", { screen: this.currentScreen });
    },

    validate(screen) {
      this.clearError(screen);

      if (this.currentScreen === 2) {
        const name = String(this.state.childName || "").trim();
        const age = Number(this.state.childAge);
        if (!name || !Number.isFinite(age) || age < 2 || age > 18) {
          this.showError(screen);
          return false;
        }
      }

      if (this.currentScreen === 3 && !String(this.state.situationDescription || "").trim()) {
        this.showError(screen);
        return false;
      }

      const group = screen.querySelector("[data-choice-group]");
      if (group) {
        const key = group.dataset.choiceGroup;
        if (!this.state[key]) {
          this.showError(screen);
          return false;
        }
      }

      const multi = screen.querySelector("[data-multi-group]");
      if (multi && (!Array.isArray(this.state.observations) || !this.state.observations.length)) {
        this.showError(screen);
        return false;
      }

      const adaptiveGroup = screen.querySelector("[data-adaptive-options]");
      if (adaptiveGroup) {
        const index = Number(adaptiveGroup.dataset.adaptiveOptions);
        const question = this.state.adaptiveQuestions[index];
        if (question && !this.state.adaptiveAnswers[question.id]) {
          this.showError(screen);
          return false;
        }
      }

      return true;
    },

    hydrate() {
      ["childName", "childAge", "situationDescription"].forEach((key) => {
        const field = this.root.querySelector(`[name="${key}"]`);
        if (field) field.value = this.state[key] || "";
      });

      this.refreshScenarioSelection();
      this.refreshObservationOptions();
      this.refreshAdaptiveQuestions();
    },

    // =========================================================================
    // NARRATIVE
    // =========================================================================

    applyNarrativeAnalysis() {
      const text = this.state.situationDescription;
      const K = PO.Knowledge;

      let detected = [];
      if (K && typeof K.findV3ObservationsInNarrative === "function") {
        detected = K.findV3ObservationsInNarrative(text);
      }

      const scenarioDetection = this.detectScenario(text);
      if (scenarioDetection?.id) {
        this.state.scenario = scenarioDetection.id;
      }

      const detectedLabels = detected.map((item) => item.observation);
      this.state.detectedObservations = detectedLabels;
      this.state.observations = this.unique(
        (this.state.observations || []).concat(detectedLabels)
      );

      this.state.narrativeAnalysis = {
        scenario: scenarioDetection,
        detectedObservations: detected
      };

      this.saveActiveSession();
      this.emit("narrative:analyzed", this.state.narrativeAnalysis);
    },

    detectScenario(text = "") {
      const value = String(text || "").toLowerCase();

      const rules = [
        ["S01", ["bedtime", "go to bed", "sleep", "pajamas", "coming out of his room", "coming out of her room"]],
        ["S02", ["morning", "getting dressed", "before school", "school bus", "leave the house"]],
        ["S03", ["homework", "worksheet", "school work", "math", "reading assignment"]],
        ["S04", ["doesn't listen", "doesnt listen", "ignored me", "repeat myself"]],
        ["S05", ["negotiates", "five more minutes", "keeps asking why", "won't take no", "wont take no"]],
        ["S06", ["tantrum", "meltdown", "screaming", "kicking", "throwing things"]],
        ["S07", ["outburst", "slammed the door", "exploded", "lost it after school"]],
        ["S08", ["screen", "ipad", "tablet", "video game", "xbox", "playstation", "youtube", "roblox"]],
        ["S09", ["brother", "sister", "sibling", "fighting with"]],
        ["S10", ["lied", "lying", "not telling the truth", "made up a story"]],
        ["S11", ["anxious", "anxiety", "worry", "worried", "panic", "scared"]],
        ["S12", ["argues", "arguing", "talks back", "last word"]]
      ];

      for (const [id, phrases] of rules) {
        const matches = phrases.filter((phrase) => value.includes(phrase));
        if (matches.length) {
          const scenario = this.getScenarios().find((item) => item.id === id);
          return {
            id,
            label: scenario?.label || id,
            matches
          };
        }
      }

      return null;
    },

    refreshScenarioSelection() {
      this.root.querySelectorAll("[data-choice-group='scenario'] [data-value]").forEach((button) => {
        button.classList.toggle("is-selected", this.state.scenario === button.dataset.value);
      });
    },

    refreshObservationOptions() {
      const target = this.root.querySelector("[data-multi-group='observations']");
      if (!target) return;

      const options = this.getScenarioObservationOptions(this.state.scenario);

      target.innerHTML = options.map((item) => `
        <button
          class="po-option ${this.state.observations.includes(item.id) ? "is-selected" : ""}"
          type="button"
          data-value="${this.escapeAttr(item.id)}"
        >
          ${this.escapeHTML(item.label)}
        </button>
      `).join("");
    },

    getScenarioObservationOptions(scenarioId) {
      const byScenario = {
        S01: ["Refuses bedtime", "Leaves bedroom repeatedly", "Needs reassurance", "Negotiates after hearing No", "Afraid of sleeping alone", "Needs multiple warnings", "Cries easily", "Easily overwhelmed"],
        S02: ["Refuses morning routine", "Needs repeated reminders", "Says Okay but doesn't act", "Forgets instructions", "Time blindness", "Easily distracted", "Refuses clothing", "Stomach aches before school"],
        S03: ["Refuses homework", "Gives up quickly", "Says I can't", "Doesn't finish work", "Afraid of making mistakes", "Perfectionism", "Easily distracted", "Avoids challenges"],
        S04: ["Doesn't listen the first time", "Needs repeated reminders", "Ignores instructions", "Pretends not to hear", "Says Okay but doesn't act", "Easily distracted", "Forgets instructions", "Argues every instruction"],
        S05: ["Negotiates after hearing No", "Constantly asks Why", "Argues every instruction", "Needs repeated reminders", "Blames others", "Makes excuses", "Wants to win everything", "Difficulty taking turns"],
        S06: ["Screams", "Throws objects", "Hits", "Kicks", "Runs away", "Cries easily", "Easily overwhelmed", "Slow recovery after becoming upset"],
        S07: ["Slams doors", "Cries easily", "Shuts down", "Withdraws", "Emotional overreaction", "Slow recovery after becoming upset", "Needs reassurance", "Easily overwhelmed"],
        S08: ["Difficulty ending screen time", "Negotiates after hearing No", "Cannot stop playing", "Screams", "Throws objects", "Pretends not to hear", "Needs multiple warnings", "Lies"],
        S09: ["Bosses siblings", "Doesn't share", "Difficulty taking turns", "Wants to win everything", "Jealous of siblings", "Hits", "Blames others", "Wants all the attention"],
        S10: ["Lies", "Hides mistakes", "Blames others", "Makes excuses", "Hides belongings", "Afraid of making mistakes", "Needs reassurance", "Steals"],
        S11: ["Worries excessively", "Needs reassurance", "Avoids new situations", "Clings to parents", "Panic before events", "Stomach aches before school", "Afraid of making mistakes", "Shuts down"],
        S12: ["Argues every instruction", "Talks back", "Interrupts constantly", "Constantly asks Why", "Negotiates after hearing No", "Blames others", "Makes excuses", "Slams doors"]
      };

      const labels = byScenario[scenarioId] || this.getObservationOptions().slice(0, 8).map((item) => item.id);

      return labels.map((label) => ({
        id: label,
        label
      }));
    },

    // =========================================================================
    // ADAPTIVE QUESTIONS
    // =========================================================================

    prepareAdaptiveQuestions() {
      if (Array.isArray(this.state.adaptiveQuestions) && this.state.adaptiveQuestions.length >= 2) {
        return;
      }

      let questions = [];

      if (PO.Engine && typeof PO.Engine.getAdaptiveQuestions === "function") {
        questions = PO.Engine.getAdaptiveQuestions(this.buildContext(), 2);
      }

      if (!Array.isArray(questions) || questions.length < 2) {
        questions = this.fallbackAdaptiveQuestions();
      }

      this.state.adaptiveQuestions = questions.slice(0, 2);
      this.refreshAdaptiveQuestions();
      this.saveActiveSession();
    },

    refreshAdaptiveQuestions() {
      [0, 1].forEach((index) => {
        const title = this.root.querySelector(`[data-adaptive-question="${index}"]`);
        const optionsWrap = this.root.querySelector(`[data-adaptive-options="${index}"]`);
        const question = this.state.adaptiveQuestions[index];

        if (!title || !optionsWrap || !question) return;

        title.textContent = question.question || question.text || "Choose the closest answer.";

        const options = Array.isArray(question.options) && question.options.length
          ? question.options
          : [
              { label: "Yes / often" },
              { label: "Sometimes" },
              { label: "No / not really" }
            ];

        const selected = this.state.adaptiveAnswers[question.id];

        optionsWrap.innerHTML = options.map((option, optionIndex) => {
          const value = option.label || String(optionIndex);
          const isSelected = selected && (
            selected.optionIndex === optionIndex ||
            selected.value === value
          );

          return `
            <button
              class="po-option ${isSelected ? "is-selected" : ""}"
              type="button"
              data-value="${this.escapeAttr(value)}"
              data-index="${optionIndex}"
            >
              ${this.escapeHTML(option.label || value)}
            </button>
          `;
        }).join("");
      });
    },

    fallbackAdaptiveQuestions() {
      return [
        {
          id: "AQ01",
          question: "When plans change unexpectedly, what usually happens?",
          measures: ["C02", "C07", "C11"],
          options: [
            { label: "They adjust fairly easily" },
            { label: "They need warning, but recover" },
            { label: "They push hard for the original plan" },
            { label: "They become worried or very upset" }
          ]
        },
        {
          id: "AQ05",
          question: "Once upset, what helps your child recover fastest?",
          measures: ["C05", "C06", "C13"],
          options: [
            { label: "Quiet closeness" },
            { label: "Physical space" },
            { label: "Movement or sensory calming" },
            { label: "Very little seems to help quickly" }
          ]
        }
      ];
    },

    // =========================================================================
    // ENGINE
    // =========================================================================

    buildContext() {
      const profile = this.getChildProfile();

      return {
        child: {
          id: this.childId(),
          name: this.state.childName,
          age: Number(this.state.childAge) || "",
          v3Memory: profile || {}
        },
        scenarioId: this.state.scenario || null,
        scenario: this.state.scenario || "general",
        situation: this.state.scenario || "general",
        description: this.state.situationDescription,
        narrative: this.state.situationDescription,
        parentNotes: this.state.situationDescription,
        observations: this.state.observations || [],
        adaptiveAnswers: this.state.adaptiveAnswers || {},
        history: profile?.sessionHistory || [],
        feedback: profile?.feedbackHistory || []
      };
    },

    runEngine() {
      const context = this.buildContext();

      if (PO.Engine && typeof PO.Engine.generatePlan === "function") {
        this.state.engineResult = PO.Engine.generatePlan(context);
      } else {
        this.state.engineResult = this.fallbackEngineResult(context);
      }

      this.saveActiveSession();
      this.updateDebugPanel();
      return this.state.engineResult;
    },

    refreshResults() {
      const result = this.state.engineResult || this.runEngine();
      if (!result) return;

      const blueprint = result.paidBlueprint || {};
      const freePreview = result.freePreview || {};
      const childName = this.state.childName || "your child";

      this.renderInto("free-preview", this.freePreviewHTML(freePreview, result));
      this.renderInto("paywall", this.paywallHTML(result));
      this.renderInto("child-profile", this.childProfileHTML(blueprint.childProfile || {}));
      this.renderInto("how-reached", this.howReachedHTML(blueprint.howWeReachedThis || {}));
      this.renderInto("situation-analysis", this.situationAnalysisHTML(blueprint.situationAnalysis || {}));
      this.renderInto("detailed-plan", this.detailedPlanHTML(blueprint.beforeDuringAfterPlan || {}));
      this.renderInto("scripts-examples", this.scriptsExamplesHTML(blueprint.scripts || {}, blueprint.exampleConversation || {}));
      this.renderInto("motivation-coparenting", this.motivationCoParentingHTML(blueprint.motivationAndRewards || {}, blueprint.coParenting || {}));
      this.renderInto("seven-day-plan", this.sevenDayPlanHTML(blueprint.sevenDayPlan || []));
      this.renderInto("progress-feedback", this.progressFeedbackHTML(
        blueprint.progressMarkers || {},
        blueprint.ifPlanFails || {},
        blueprint.professionalSupport || {},
        blueprint.disclaimer || ""
      ));
    },

    renderInto(key, html) {
      const target = this.root.querySelector(`[data-result="${key}"]`);
      if (target) target.innerHTML = html;
    },

    // =========================================================================
    // RESULT HTML
    // =========================================================================

    freePreviewHTML(preview, result) {
      const drivers = Array.isArray(preview.topDrivers) ? preview.topDrivers : [];
      const confidence = preview.confidencePreview || result.confidenceDetail || {};

      return `
        <div class="po-result-card po-cinematic-card">
          <p class="po-result-kicker">Your free insight</p>
          <h1 class="po-result-title">We found a meaningful pattern.</h1>
          <p class="po-result-summary">
            ${this.escapeHTML(preview.patternSummary || result.workingUnderstanding?.summary || result.why || "")}
          </p>

          <div class="po-confidence">
            <div class="po-confidence-head">
              <span>Working confidence</span>
              <strong>${this.escapeHTML(String(confidence.percent || 0))}%</strong>
            </div>
            <div class="po-meter"><span style="width:${Number(confidence.percent || 0)}%"></span></div>
            <p>${this.escapeHTML(confidence.language || "")}</p>
          </div>

          <div class="po-driver-list">
            ${drivers.map((driver, index) => `
              <div class="po-driver">
                <span class="po-driver-rank">${index + 1}</span>
                <div>
                  <h3 class="po-mini-title">${this.escapeHTML(driver.label || "")}</h3>
                  <p class="po-result-text">${this.escapeHTML(driver.meaning || "")}</p>
                </div>
              </div>
            `).join("")}
          </div>

          <div class="po-mini-card">
            <h3 class="po-mini-title">One immediate insight</h3>
            <p class="po-result-text">${this.escapeHTML(preview.oneImmediateInsight || "")}</p>
          </div>
        </div>
      `;
    },

    paywallHTML(result) {
      const name = this.state.childName || "your child";
      return `
        <div class="po-result-card po-paywall-card">
          <p class="po-result-kicker">Personalized Parenting Blueprint</p>
          <h1 class="po-result-title">Your full plan for ${this.escapeHTML(name)} is ready.</h1>
          <p class="po-result-text">
            Unlock the detailed analysis, evidence, scripts, examples, reward ideas, co-parenting guidance, and 7-day plan.
          </p>

          <div class="po-blueprint-includes">
            ${[
              "A personalized child profile",
              "How we reached the conclusion",
              "The parent-child interaction loop",
              "Before / during / after strategy",
              "Warm, firm, and repair scripts",
              "A realistic example conversation",
              "Rewards and motivation",
              "Co-parenting alignment",
              "A complete 7-day action plan",
              "Progress markers and fallback steps"
            ].map((item) => `<div>✓ ${this.escapeHTML(item)}</div>`).join("")}
          </div>

          <p class="po-result-note">
            Checkout still needs to be connected. For testing, this button unlocks the Blueprint immediately.
          </p>
        </div>
      `;
    },

    childProfileHTML(profile) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">Your child profile</p>
          <h1 class="po-result-title">${this.escapeHTML(profile.headline || "A working profile")}</h1>
          <p class="po-result-summary">${this.escapeHTML(profile.summary || "")}</p>

          ${this.tagSection("Strengths we may be seeing", profile.strengths)}
          ${this.tagSection("Likely stress triggers", profile.stressTriggers)}
          ${this.tagSection("Responds best to", profile.respondsBestTo)}

          <p class="po-result-note">${this.escapeHTML(profile.note || "")}</p>
        </div>
      `;
    },

    howReachedHTML(evidence) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">How we reached this conclusion</p>
          <h1 class="po-result-title">We looked for details that appeared together.</h1>

          ${evidence.narrativeExcerpt ? `
            <div class="po-quote-card">
              “${this.escapeHTML(evidence.narrativeExcerpt)}”
            </div>
          ` : ""}

          ${this.listSection("What you described", evidence.observations)}
          ${this.listSection("Evidence for the primary pattern", evidence.primaryEvidence)}
          ${this.listSection("Evidence for the secondary pattern", evidence.secondaryEvidence)}

          <p class="po-result-summary">${this.escapeHTML(evidence.explanation || "")}</p>
        </div>
      `;
    },

    situationAnalysisHTML(analysis) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">Why this keeps repeating</p>
          <h1 class="po-result-title">${this.escapeHTML(analysis.headline || "")}</h1>
          <p class="po-result-summary">${this.escapeHTML(analysis.keyInsight || "")}</p>

          ${analysis.primaryDriver ? `
            <div class="po-mini-card">
              <h3 class="po-mini-title">${this.escapeHTML(analysis.primaryDriver.label || "")}</h3>
              <p class="po-result-text">${this.escapeHTML(analysis.primaryDriver.explanation || "")}</p>
            </div>
          ` : ""}

          ${analysis.secondaryDriver ? `
            <div class="po-mini-card">
              <h3 class="po-mini-title">${this.escapeHTML(analysis.secondaryDriver.label || "")}</h3>
              <p class="po-result-text">${this.escapeHTML(analysis.secondaryDriver.explanation || "")}</p>
            </div>
          ` : ""}

          ${this.cycleSection(analysis.commonLoop)}

          <div class="po-callout">
            <strong>This may not be:</strong>
            <p>${this.escapeHTML(analysis.whatThisMayNotBe || "")}</p>
          </div>
        </div>
      `;
    },

    detailedPlanHTML(plan) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">Your personalized plan</p>
          <h1 class="po-result-title">${this.escapeHTML(plan.headline || "")}</h1>
          ${plan.ageNote ? `<p class="po-result-note">${this.escapeHTML(plan.ageNote)}</p>` : ""}

          ${this.numberedSection("Before the situation", plan.before)}
          ${this.numberedSection("During the situation", plan.during)}
          ${this.numberedSection("After the situation", plan.after)}

          <div class="po-script-card">
            <p class="po-mini-title">The first move tonight</p>
            <blockquote>${this.escapeHTML(plan.firstMoveTonight || "")}</blockquote>
          </div>
        </div>
      `;
    },

    scriptsExamplesHTML(scripts, example) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">Scripts and examples</p>
          <h1 class="po-result-title">Use language that fits the moment.</h1>

          ${this.scriptBlock("Warm version", scripts.warm)}
          ${this.scriptBlock("Firm version", scripts.firm)}
          ${this.scriptBlock("Repair version", scripts.repair)}

          <div class="po-instead-grid">
            <div class="po-instead bad">
              <span>Instead of</span>
              <p>“${this.escapeHTML(example.insteadOf || scripts.insteadOf || "")}”</p>
            </div>
            <div class="po-instead good">
              <span>Try</span>
              <p>“${this.escapeHTML(example.tryInstead || scripts.tryInstead || "")}”</p>
            </div>
          </div>

          <div class="po-conversation">
            <h3 class="po-mini-title">${this.escapeHTML(example.headline || "Example conversation")}</h3>
            ${this.conversationLine("Parent", example.parentOpening)}
            ${this.conversationLine(this.state.childName || "Child", example.likelyChildResponse)}
            ${this.conversationLine("Parent", example.parentBoundary)}
            ${this.conversationLine("Later", example.repair)}
          </div>
        </div>
      `;
    },

    motivationCoParentingHTML(motivation, coParenting) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">Motivation and co-parenting</p>
          <h1 class="po-result-title">Help the plan work beyond one difficult moment.</h1>

          ${this.tagSection("Likely motivators", motivation.likelyMotivators)}
          ${this.listSection("Reward ideas", motivation.rewardIdeas)}
          ${this.listSection("Rewards or approaches that may backfire", motivation.avoid)}

          <div class="po-callout">
            <strong>Reward principle</strong>
            <p>${this.escapeHTML(motivation.principle || "")}</p>
          </div>

          <div class="po-divider"></div>

          <h2 class="po-section-title">${this.escapeHTML(coParenting.headline || "How adults can stay aligned")}</h2>
          <p class="po-result-text">${this.escapeHTML(coParenting.familyContext || "")}</p>
          ${this.listSection("Use this approach", [
            coParenting.primaryGuidance,
            coParenting.secondaryGuidance
          ].filter(Boolean))}
          ${this.scriptBlock("Shared parent script", coParenting.sharedScript)}
          ${this.listSection("Avoid", coParenting.avoid)}
        </div>
      `;
    },

    sevenDayPlanHTML(plan) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">Your 7-day plan</p>
          <h1 class="po-result-title">Test one explanation carefully.</h1>

          <div class="po-timeline">
            ${(plan || []).map((block) => `
              <div class="po-timeline-item">
                <span class="po-timeline-dot"></span>
                <div>
                  <p class="po-result-kicker">${this.escapeHTML(block.day || "")}</p>
                  <h3 class="po-mini-title">${this.escapeHTML(block.title || "")}</h3>
                  <ul class="po-mini-list">
                    ${(block.actions || []).map((item) => `<li>${this.escapeHTML(item)}</li>`).join("")}
                  </ul>
                </div>
              </div>
            `).join("")}
          </div>
        </div>
      `;
    },

    progressFeedbackHTML(progress, fallback, support, disclaimer) {
      return `
        <div class="po-result-card">
          <p class="po-result-kicker">What to track</p>
          <h1 class="po-result-title">${this.escapeHTML(progress.headline || "What improvement should look like")}</h1>

          ${this.listSection("Early signs of progress", progress.earlySigns)}
          ${this.listSection("Track these details", progress.whatToTrack)}

          <div class="po-callout">
            <strong>Success does not mean perfection</strong>
            <p>${this.escapeHTML(progress.successRule || "")}</p>
          </div>

          <div class="po-divider"></div>

          <h2 class="po-section-title">${this.escapeHTML(fallback.headline || "If the first plan does not work")}</h2>
          ${this.listSection("", fallback.steps)}

          <div class="po-divider"></div>

          <h2 class="po-section-title">${this.escapeHTML(support.headline || "When additional support may be useful")}</h2>
          <p class="po-result-text">${this.escapeHTML(support.message || "")}</p>
          ${this.listSection("", support.generalIndicators)}

          <p class="po-result-note">${this.escapeHTML(disclaimer || "")}</p>
        </div>
      `;
    },

    // =========================================================================
    // SMALL HTML HELPERS
    // =========================================================================

    tagSection(title, items = []) {
      const clean = (items || []).filter(Boolean);
      if (!clean.length) return "";

      return `
        <div>
          <h3 class="po-mini-title">${this.escapeHTML(title)}</h3>
          <div class="po-chip-row">
            ${clean.map((item) => `<span class="po-chip">${this.escapeHTML(item)}</span>`).join("")}
          </div>
        </div>
      `;
    },

    listSection(title, items = []) {
      const clean = (items || []).filter(Boolean);
      if (!clean.length) return "";

      return `
        <div>
          ${title ? `<h3 class="po-mini-title">${this.escapeHTML(title)}</h3>` : ""}
          <ul class="po-mini-list">
            ${clean.map((item) => `<li>${this.escapeHTML(typeof item === "string" ? item : item.message || JSON.stringify(item))}</li>`).join("")}
          </ul>
        </div>
      `;
    },

    numberedSection(title, items = []) {
      const clean = (items || []).filter(Boolean);
      if (!clean.length) return "";

      return `
        <div>
          <h3 class="po-section-title">${this.escapeHTML(title)}</h3>
          <ol class="po-result-steps">
            ${clean.map((item, index) => `
              <li>
                <span>${index + 1}</span>
                <p>${this.escapeHTML(item)}</p>
              </li>
            `).join("")}
          </ol>
        </div>
      `;
    },

    cycleSection(items = []) {
      const clean = (items || []).filter(Boolean);
      if (!clean.length) return "";

      return `
        <div class="po-cycle">
          ${clean.map((item, index) => `
            <div>${this.escapeHTML(item)}</div>
            ${index < clean.length - 1 ? "<span>↓</span>" : ""}
          `).join("")}
        </div>
      `;
    },

    scriptBlock(title, text) {
      if (!text) return "";
      return `
        <div class="po-script-card">
          <p class="po-mini-title">${this.escapeHTML(title)}</p>
          <blockquote>${this.escapeHTML(text)}</blockquote>
        </div>
      `;
    },

    conversationLine(label, text) {
      if (!text) return "";
      return `
        <div class="po-conversation-line">
          <strong>${this.escapeHTML(label)}</strong>
          <p>${this.escapeHTML(text)}</p>
        </div>
      `;
    },

    // =========================================================================
    // STORAGE
    // =========================================================================

    saveActiveSession() {
      try {
        window.sessionStorage.setItem(this.sessionStorageKey, JSON.stringify({
          currentScreen: this.currentScreen,
          state: this.state
        }));
      } catch (error) {
        console.warn("Parenting Optimize session save unavailable", error);
      }
    },

    clearActiveSession() {
      try {
        window.sessionStorage.removeItem(this.sessionStorageKey);
      } catch {}
    },

    loadProfiles() {
      try {
        return JSON.parse(window.localStorage.getItem(this.profileStorageKey) || "{}");
      } catch {
        return {};
      }
    },

    saveProfiles(profiles) {
      try {
        window.localStorage.setItem(this.profileStorageKey, JSON.stringify(profiles || {}));
      } catch (error) {
        console.warn("Parenting Optimize profile save unavailable", error);
      }
    },

    getChildProfile() {
      const profiles = this.loadProfiles();
      return profiles[this.childId()] || null;
    },

    persistChildProfile(result = null) {
      if (!this.state.childName) return;

      const profiles = this.loadProfiles();
      const id = this.childId();
      const current = profiles[id] || {
        childId: id,
        childName: this.state.childName,
        age: Number(this.state.childAge) || "",
        sessionHistory: [],
        feedbackHistory: []
      };

      let next = current;

      if (result?.memoryPatch && PO.Engine?.applyChildProfilePatch) {
        next = PO.Engine.applyChildProfilePatch(current, result.memoryPatch);
      } else {
        next = {
          ...current,
          childName: this.state.childName,
          age: Number(this.state.childAge) || "",
          lastUpdated: new Date().toISOString()
        };
      }

      profiles[id] = next;
      this.saveProfiles(profiles);
    },

    handleFeedback(result) {
      const engineResult = this.state.engineResult || this.runEngine();
      this.persistChildProfile(engineResult);

      const profiles = this.loadProfiles();
      const id = this.childId();
      const current = profiles[id] || {};
      const patch = PO.Engine?.learnFromFeedback
        ? PO.Engine.learnFromFeedback(
            { ...this.buildContext(), activePlan: engineResult },
            {
              result,
              planId: engineResult?.id || null,
              strategy: engineResult?.title || "current-plan"
            }
          )
        : null;

      profiles[id] = patch && PO.Engine?.applyChildProfilePatch
        ? PO.Engine.applyChildProfilePatch(current, patch)
        : current;

      this.saveProfiles(profiles);
      this.archiveSession(result);
      this.clearActiveSession();

      const message =
        result === "worked"
          ? "Good. We’ll remember that this kind of approach may work for this child."
          : result === "somewhat"
            ? "Helpful to know. We’ll keep the core idea and adjust the details."
            : "Important feedback. The next session should test a different explanation.";

      window.alert(message);
    },

    archiveSession(feedbackResult = null) {
      try {
        const history = JSON.parse(window.localStorage.getItem(this.historyStorageKey) || "[]");

        history.push({
          id: this.uid("session"),
          childId: this.childId(),
          childName: this.state.childName,
          age: this.state.childAge,
          scenario: this.state.scenario,
          observations: this.state.observations,
          adaptiveAnswers: this.state.adaptiveAnswers,
          result: this.state.engineResult,
          feedbackResult,
          createdAt: new Date().toISOString()
        });

        window.localStorage.setItem(
          this.historyStorageKey,
          JSON.stringify(history.slice(-50))
        );
      } catch (error) {
        console.warn("Parenting Optimize history save unavailable", error);
      }
    },

    startOver() {
      this.persistChildProfile(this.state.engineResult);
      this.archiveSession(null);
      this.clearActiveSession();

      const paid = this.state.paidUnlocked;
      this.state = DEFAULT_STATE();
      this.state.paidUnlocked = paid;
      this.currentScreen = 1;

      this.render();
      this.showScreen(1, { save: false });
    },

    // =========================================================================
    // DATA
    // =========================================================================

    getScenarios() {
      if (PO.Knowledge && typeof PO.Knowledge.listV3Scenarios === "function") {
        const list = PO.Knowledge.listV3Scenarios();
        if (Array.isArray(list) && list.length) {
          return list.map((item) => ({
            id: item.id,
            label: item.label
          }));
        }
      }

      return [
        { id: "S01", label: "Bedtime Struggles" },
        { id: "S02", label: "Morning Routine" },
        { id: "S03", label: "Homework Refusal" },
        { id: "S04", label: "Doesn't Listen" },
        { id: "S05", label: "Negotiates Everything" },
        { id: "S06", label: "Tantrums / Meltdowns" },
        { id: "S07", label: "Emotional Outbursts" },
        { id: "S08", label: "Screen Time Battles" },
        { id: "S09", label: "Sibling Conflict" },
        { id: "S10", label: "Lying" },
        { id: "S11", label: "Anxiety / Worry" },
        { id: "S12", label: "Arguing" }
      ];
    },

    getObservationOptions() {
      if (PO.Knowledge && typeof PO.Knowledge.listV3Observations === "function") {
        return PO.Knowledge.listV3Observations().map((item) => ({
          id: item.label,
          label: item.label
        }));
      }
      return [];
    },

    fallbackEngineResult() {
      return {
        id: this.uid("fallback"),
        freePreview: {
          patternSummary: "We are beginning to see the pattern.",
          topDrivers: [],
          confidencePreview: {
            percent: 50,
            language: "More answers are needed."
          },
          oneImmediateInsight: "Use one calm, repeatable change."
        },
        paidBlueprint: {
          childProfile: {},
          howWeReachedThis: {},
          situationAnalysis: {},
          beforeDuringAfterPlan: {},
          scripts: {},
          exampleConversation: {},
          motivationAndRewards: {},
          coParenting: {},
          sevenDayPlan: [],
          progressMarkers: {},
          ifPlanFails: {},
          professionalSupport: {},
          disclaimer: "Educational guidance only."
        }
      };
    },

    // =========================================================================
    // DEBUG
    // =========================================================================

    updateDebugPanel() {
      if (!this.debugMode) return;
      const output = this.root.querySelector("[data-debug-output]");
      if (!output) return;

      output.textContent = JSON.stringify({
        screen: this.currentScreen,
        paidUnlocked: this.state.paidUnlocked,
        scenario: this.state.scenario,
        observations: this.state.observations,
        detectedObservations: this.state.detectedObservations,
        adaptiveAnswers: this.state.adaptiveAnswers,
        engineResult: this.state.engineResult
      }, null, 2);
    },

    // =========================================================================
    // UTILITIES
    // =========================================================================

    getCurrentScreen() {
      return this.root.querySelector(`.po-screen[data-screen="${this.currentScreen}"]`);
    },

    showError(screen) {
      screen?.querySelector("[data-error]")?.classList.add("is-visible");
    },

    clearError(screen) {
      screen?.querySelector("[data-error]")?.classList.remove("is-visible");
    },

    childId() {
      const clean = String(this.state.childName || "child")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

      return `child-${clean || "profile"}`;
    },

    unique(list = []) {
      return Array.from(new Set((list || []).filter(Boolean)));
    },

    uid(prefix = "po") {
      return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    },

    emit(name, detail) {
      document.dispatchEvent(
        new CustomEvent(`parentingOptimizeV3:${name}`, { detail })
      );
    },

    escapeHTML(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    escapeAttr(value) {
      return this.escapeHTML(value);
    }
  };

  document.addEventListener("DOMContentLoaded", () => {
    PO.AppV3.init();
  });
})(window, document);
